import { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { logError } from "~src/utils/logger";

type FilterTypes =
  | 'discover_brands'
  | 'best_selling_products'
  | 'discover_brands_columns'
  | 'best_selling_products_columns'
  | 'best_selling_products_selected'
  | 'best_selling_products_search'
  | 'discover_brands_ordering'
  | 'best_selling_products_ordering'
  | 'discover_ads'
  | 'discover_ads_search';

// Debounce delay in milliseconds
const DEBOUNCE_DELAY = 300;

/**
 * A custom hook that persists state in localStorage between page navigations.
 *
 * This hook is specifically designed to persist filter states across navigation events,
 * solving the problem of filters being reset when users navigate away from a page and then back.
 *
 * Features:
 * - Debounced localStorage writes (300ms) to prevent blocking the main thread
 * - Automatic cleanup on unmount to cancel pending writes
 * - Graceful error handling for corrupted localStorage data
 *
 * @template T - The type of the state to persist
 * @param {FilterTypes} filterType - A unique identifier for this state in localStorage
 * @param {T} defaultValue - The default value to use if nothing is found in localStorage
 * @returns {[T, Dispatch<SetStateAction<T>>]} - A stateful value and a function to update it,
 *                                              identical to the return value of React's useState
 *
 * @example
 * // Usage in a component:
 * const [filters, setFilters] = useFilterPersistence<BrandDiscoverFilters>(
 *   'discover_brands',
 *   DefaultBrandDiscoverFilters
 * );
 */
export default function useFilterPersistence<T>(
  filterType: FilterTypes,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  // Use filterType to create a unique localStorage key
  const storageKey = `filters_${filterType}`;

  // Track pending timeout for cleanup
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize state from localStorage or use default value
  const [state, setState] = useState<T>(() => {
    try {
      const storedValue = localStorage.getItem(storageKey);
      return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
    } catch (error) {
      logError(`Error parsing stored ${filterType} filters: ${error}`);
      return defaultValue;
    }
  });

  // Debounced save to localStorage whenever state changes
  useEffect(() => {
    // Clear any pending writes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Schedule new write after debounce delay
    timeoutRef.current = setTimeout(() => {
      try {
        if (state === undefined) {
          localStorage.removeItem(storageKey);
        } else {
          localStorage.setItem(storageKey, JSON.stringify(state));
        }
      } catch (error) {
        logError(`Error writing to localStorage for ${filterType}: ${error}`);
      }
    }, DEBOUNCE_DELAY);

    // Cleanup on unmount or before next effect
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [state, storageKey, filterType]);

  return [state, setState];
} 